import React, { useCallback, useMemo, useState } from "react";
import { mergeClass, replaceUrlCdn } from "../../utils/common";

type CdnImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  fallback?: boolean;
  cdnParams?: CdnParam;
};

export default function CdnImage({
  src,
  alt = "",
  className,
  fallback,
  loading,
  cdnParams,
  fetchPriority,
  ...rest
}: CdnImageProps) {
  const cdnSrc = useMemo(() => (src ? replaceUrlCdn(src) : undefined), [src]);
  const cdnSrcWithParams = useMemo(() => {
    if (cdnSrc && cdnParams) {
      let url = cdnSrc;
      const filters: string[] = [];

      // Handle 'quality' param as filters:quality(100)/
      if (cdnParams.quality) {
        filters.push(`quality(${cdnParams.quality})`);
      }

      // Add more filters here if needed

      if (filters.length > 0) {
        // Insert filters before the path (after domain)
        const urlObj = new URL(url);
        urlObj.pathname = `/filters:${filters.join(":")}/${urlObj.pathname.replace(/^\//, "")}`;
        url = urlObj.toString();
      }

      // Add other params as query string (excluding 'quality')
      const otherParams = { ...cdnParams };
      delete otherParams.quality;
      const query = new URLSearchParams(
        Object.fromEntries(
          Object.entries(otherParams).map(([k, v]) => [k, v?.toString() ?? ""]),
        ),
      ).toString();

      if (query) {
        url += (url.includes("?") ? "&" : "?") + query;
      }

      return url;
    }
    return cdnSrc;
  }, [cdnSrc, cdnParams]);

  // Fallback order: cdnSrcWithParams -> cdnSrc -> src
  const sources = useMemo(
    () => [cdnSrcWithParams, cdnSrc, src].filter(Boolean) as string[],
    [cdnSrcWithParams, cdnSrc, src],
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleError = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1 < sources.length ? prev + 1 : prev));
  }, [sources.length]);

  if (!src && !fallback) return null;

  return (
    <img
      src={sources[currentIndex]}
      alt={alt || ""}
      className={mergeClass("object-cover", className)}
      loading={loading}
      onError={handleError}
      fetchPriority={fetchPriority}
      {...rest}
    />
  );

  // return (
  //   <picture>
  //     {currentIndex === 0 && cdnSrcWithParams && (
  //       <source
  //         srcSet={cdnSrcWithParams}
  //         type={cdnParams?.format ? `image/${cdnParams.format}` : undefined}
  //       />
  //     )}
  //     <img
  //       src={sources[currentIndex]}
  //       alt={alt || ""}
  //       className={mergeClass("object-cover", className)}
  //       loading={loading}
  //       onError={handleError}
  //       {...rest}
  //     />
  //   </picture>
  // );
}
